/**
 * Recent Artifacts Store
 *
 * Tracks the last N artifacts the user interacted with — opening a monitor
 * card, clicking a sidebar item, viewing an objective, etc. Feeds the
 * "Recent" section at the top of the ArtifactPicker so the artifacts the
 * user is currently thinking about surface first.
 *
 * Dedupes by (type, id) so re-interacting with the same artifact moves it
 * to the front instead of creating a duplicate entry.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Artifact } from '@customTypes/artifact';
import { workspaceScopedStorage } from '@stores/utils';

const RECENT_LIMIT = 4;

interface RecentArtifactsState {
  recent: Artifact[];
}

interface RecentArtifactsActions {
  /** Push an artifact to the front. If it's already in the list, it moves up. */
  markInteracted: (artifact: Artifact) => void;
  clear: () => void;
}

export const useRecentArtifactsStore = create<
  RecentArtifactsState & RecentArtifactsActions
>()(
  persist(
    (set) => ({
      recent: [],

      markInteracted: (artifact) =>
        set((state) => {
          const filtered = state.recent.filter(
            (a) => !(a.type === artifact.type && a.id === artifact.id),
          );
          return { recent: [artifact, ...filtered].slice(0, RECENT_LIMIT) };
        }),

      clear: () => set({ recent: [] }),
    }),
    {
      name: 'recentArtifacts',
      storage: workspaceScopedStorage<RecentArtifactsState>('recentArtifacts'),
      version: 1,
    },
  ),
);
